import { defineStore } from "pinia";
import moment from "moment";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";

import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { Comment } from "~/types/comment";
import {
  Lead,
  Opportunity,
  OpportunityStage,
  OpportunityType,
} from "~/projects/crm/types";
import { OpportunityView } from "~/projects/crm/types/enums//opportunityView";
import { OpportunityByStage } from "~/projects/crm/types/opportunityByStage";

interface OpportunityAttachment {
  file: File;
  name: string;
  description?: string;
}

// Recursively serialize a value into FormData using Laravel's bracket notation
// (e.g. assigned_users[0][id]) so nested opportunity data survives a multipart
// request.
const appendToFormData = (formData: FormData, key: string, value: any) => {
  if (value === null || value === undefined) return;

  if (value instanceof File || value instanceof Blob) {
    formData.append(key, value);
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendToFormData(formData, `${key}[${index}]`, item);
    });
  } else if (typeof value === "object") {
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      appendToFormData(formData, `${key}[${nestedKey}]`, nestedValue);
    });
  } else if (typeof value === "boolean") {
    formData.append(key, value ? "1" : "0");
  } else {
    formData.append(key, value as string);
  }
};

const buildOpportunityFormData = (
  opportunity: Opportunity,
  attachments: OpportunityAttachment[] = [],
) => {
  const formData = new FormData();

  Object.entries(opportunity).forEach(([key, value]) => {
    appendToFormData(formData, key, value);
  });

  attachments.forEach((attachment, index) => {
    if (attachment?.file) {
      formData.append(`attachments[${index}][file]`, attachment.file);
    }
    formData.append(`attachments[${index}][name]`, attachment.name ?? "");
    formData.append(
      `attachments[${index}][description]`,
      attachment.description ?? "",
    );
  });

  return formData;
};

export const useOpportunitiesStore = defineStore("opportunitiesStore", {
  state: () => ({
    _opportunity: {} as Opportunity,
    _view: OpportunityView.BOARD,
    tableLoader: true,
    boardLoader: true,
    _tableData: [] as Array<Opportunity>,
    _selectedOpportunityForEdit: {} as Opportunity,
    _opportunityStage: [] as OpportunityByStage[],
    multipleSelection: [] as Array<Opportunity>,
    _filterData: {},
    _opportunityTasks: {} as any,
    _pagination: {} as Pagination,
    showOpportunityEditModal: false,
    showBulkUpload: false,
    showTableOpportunityEditModal: false,
    showOpportunitiesFiltersEditModal: false,
    showOpportunityCreateModal: false,
    showKanbanEditDialog: false,
    showKanbanCreateDialog: false,
    selectedLeadData: {} as Lead,
    createdStage: "" as OpportunityStage,

    // for opportunity Comment
    _opportunityComments: [] as Comment[],
  }),

  getters: {
    opportunity: (state) => state._opportunity,
    showEditOptions: (state) => state.multipleSelection.length > 0,
    tableData: (state) => state._tableData,
    opportunityStage: (state) => state._opportunityStage,
    selectedOpportunityForEdit: (state) => state._selectedOpportunityForEdit,
    pagination: (state) => state._pagination,
    view: (state) => state._view,
    opportunityTasks: (state) => state._opportunityTasks,
    comments: (state) => state._opportunityComments,
  },
  actions: {
    async getOpportunities(pageNo = 1, sortBy = "id", ascending = false) {
      this.tableLoader = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `crm/opportunities?paginate=true&page=${pageNo}&sort_column=${sortBy}&dir=${sortDir}`;
      for (const key in this._filterData) {
        // @ts-ignore
        url += `&${key}=${this._filterData[key]}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<Opportunity>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this.$state._tableData = data;
        this.$state._pagination = pagination;
        this.tableLoader = false;
      }
    },
    async exportTable() {
      let url = `crm/opportunities/export?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `${key}=${value}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"opportunities-report"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },
    async getOpportunity(opportunityId: number) {
      const { response, error } = await getRequest<Opportunity>(
        `crm/opportunities/${opportunityId}`,
        false,
      );
      if (!error) {
        this.$state._opportunity = response.data;
      }
    },
    async getOpportunityTasks(opportunityId: number) {
      const { response, error } = await getRequest(
        `crm/opportunities/${opportunityId}/tasks`,
      );
      if (!error) {
        this.$state._opportunityTasks = response.data;
      }
    },

    async getOpportunityComments(id: number) {
      const { response, error } = await getRequest<Comment[]>(
        `crm/opportunities/${id}/comments`,
      );
      if (!error) {
        this._opportunityComments = response.data;
      }
    },

    async getStageWiseOpportunities() {
      this.boardLoader = true;
      let url = `crm/opportunities/with-stage?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<OpportunityByStage[]>(url);
      if (!error) {
        this.$state._opportunityStage = response.data;
        this.boardLoader = false;
      }
    },
    // Appends the next slice of one board column.
    //
    // The column and the paging cursor are arguments rather than keys parked on
    // _filterData, because `stage` there is the agent's own stage filter
    // (Filters.vue). Writing the scrolled column's id over it re-scoped every
    // later request to that one column — and updateOpportunity only ever cleared
    // `offset`, so the post-save refetch came back as a one-column board.
    async getStageOpportunitiesByOffset(
      stageId: number,
      offset: number,
      length = 10,
    ) {
      let url = `crm/opportunities/by-offset?stage=${encodeURIComponent(
        stageId,
      )}&offset=${offset}&length=${length}`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        // The column being paged is narrower than any stage the agent could
        // have filtered to, so it wins; the rest of the filters still apply.
        if (key === "stage" || key === "offset" || key === "length") continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<OpportunityByStage[]>(url);

      if (!error) {
        const { data } = response;

        if (!data || data.length === 0) {
          return;
        }

        // Only a column already on the board can be scrolled, so a miss means
        // the board was replaced mid-request — appending would build a group
        // with no name or count behind it.
        const stageGroup = this.$state._opportunityStage.find(
          (group) => group.id === stageId,
        );
        if (!stageGroup) return;

        // Only append if the column is still exactly where this request started.
        // Nothing cancels an in-flight page, so a response can land after the
        // board has been refetched and rebuilt — appending it then would splice
        // a later slice onto a fresh first page and leave a hole in the list.
        if (stageGroup.opportunities.length !== offset) return;

        stageGroup.opportunities.push(...data);
      }
    },
    async move(opportunityData: Partial<Opportunity>) {
      const { response, error } = await postRequest<Opportunity>(
        `crm/opportunities/${opportunityData.id}/move`,
        opportunityData,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async createOpportunity(
      opportunity: Opportunity,
      attachments: OpportunityAttachment[] = [],
    ) {
      const hasAttachments = attachments.length > 0;
      const { response, error } = await postRequest<Opportunity>(
        "crm/opportunities",
        hasAttachments
          ? buildOpportunityFormData(opportunity, attachments)
          : opportunity,
        true,
        hasAttachments
          ? { headers: { "Content-Type": "multipart/form-data" } }
          : ({} as any),
      );
      if (!error) {
        this._filterData = {};
        return response;
      }
      return null;
    },
    async updateOpportunity(
      opportunities: Opportunity,
      attachments: OpportunityAttachment[] = [],
    ) {
      const hasAttachments = attachments.length > 0;

      // Laravel can't parse multipart bodies on a real PUT, so when files are
      // present we POST with method spoofing; otherwise keep the JSON PUT.
      let response;
      let error;
      if (hasAttachments) {
        const formData = buildOpportunityFormData(opportunities, attachments);
        formData.append("_method", "put");
        ({ response, error } = await postRequest<Opportunity>(
          `crm/opportunities/${opportunities.id}`,
          formData,
          true,
          { headers: { "Content-Type": "multipart/form-data" } },
        ));
      } else {
        ({ response, error } = await putRequest<Opportunity>(
          `crm/opportunities/${opportunities.id}`,
          opportunities,
        ));
      }
      if (!error) {
        return response.data;
      }
      return null;
    },
    async deleteOpportunity(opportunities: Opportunity) {
      return await deleteRequest(`crm/opportunities/${opportunities.id}`);
    },
    async getOpportunitiesCollection() {
      const { response, error } = await getRequest<Opportunity[]>(
        "crm/opportunities",
      );

      if (!error) {
        return response.data;
      }
      return null;
    },
    // mutations
    setSelectedLeadData(lead: Lead) {
      this.selectedLeadData = lead;
    },
    selectionChange(arr: Array<Opportunity>) {
      this.multipleSelection = arr;
    },
    toggleOpportunitiesFiltersEditModal() {
      this.showOpportunitiesFiltersEditModal =
        !this.showOpportunitiesFiltersEditModal;
    },
    closeOpportunityEditModal() {
      this.showOpportunityEditModal = false;
    },
    openOpportunityEditModal() {
      this.showOpportunityEditModal = true;
    },
    closeTableOpportunityEditModal() {
      this.showTableOpportunityEditModal = false;
    },
    openTableOpportunityEditModal() {
      this.showTableOpportunityEditModal = true;
    },
    setEditOpportunityData(opportunity: Opportunity) {
      this._selectedOpportunityForEdit = opportunity;
      this._opportunity = opportunity;
    },
    setFilterData(filter: any) {
      this._filterData = filter;
    },
    toggleOpportunityCreateModal() {
      this.showOpportunityCreateModal = !this.showOpportunityCreateModal;
    },
    closeOpportunityCreateModal() {
      this.showOpportunityCreateModal = false;
    },
    setView(view: OpportunityView) {
      this.$state._view = view;
    },
    relocateOpportunity(
      newStageGroupName: string,
      opportunityId: number,
      updatedStageId: number,
      updatedStageObject: OpportunityStage,
      newPosition: number,
    ) {
      let opportunityToMove = {} as Opportunity;
      let previousGroup:
        | { name: string; opportunities: Opportunity[]; count: number }
        | undefined;

      this._opportunityStage.find((group) => {
        const opportunityIndex = group.opportunities.findIndex(
          (opportunity) => opportunity.id === opportunityId,
        );
        if (opportunityIndex !== -1) {
          opportunityToMove = group.opportunities.splice(
            opportunityIndex,
            1,
          )[0];
          previousGroup = group;
        }
      });

      if (!opportunityToMove) {
        console.log(`Opportunity with ID ${opportunityId} not found.`);
        return;
      }

      if (previousGroup) {
        previousGroup.count = Math.max(0, (previousGroup.count || 0) - 1);
      }

      opportunityToMove.stage_id = updatedStageId;
      opportunityToMove.stage = updatedStageObject;
      opportunityToMove.position = newPosition;

      let targetGroup = this._opportunityStage.find(
        (group) => group.name === newStageGroupName,
      );

      if (!targetGroup) {
        targetGroup = { name: newStageGroupName, opportunities: [], count: 0 };
        this._opportunityStage.push(targetGroup);
      }

      targetGroup.count = (targetGroup.count || 0) + 1;

      const insertIndex = targetGroup.opportunities.findIndex(
        (opportunity) => opportunity.position > newPosition,
      );

      if (insertIndex === -1) {
        targetGroup.opportunities.push(opportunityToMove);
      } else {
        targetGroup.opportunities.splice(insertIndex, 0, opportunityToMove);
      }
    },
  },
});
